import {
  ImageScaleView,
  ImageView,
  View,
  ViewOpts,
} from '@play-co/timestep-core/ui';
import { Avatar } from 'src/game/components/shared/Avatar';
import i18n from 'src/lib/i18n/i18n';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { Feature } from 'src/replicant/analytics';
import { TeamMemberData } from 'src/replicant/team/team.ruleset';

type TeamMemberEntryOpts = ViewOpts & {
  onEntryClick?: () => Promise<void>;
};

export class TeamMemberEntry extends ButtonScaleView {
  private readonly bg: ImageScaleView;
  private readonly positionText: LangBitmapFontTextView;
  private readonly positionIcon: ImageScaleView;
  private readonly avatar: Avatar;
  private readonly name: LangBitmapFontTextView;
  private readonly level: LangBitmapFontTextView;
  private readonly teamLabel: LangBitmapFontTextView;
  private readonly leader: View;
  private readonly leaderLabel: LangBitmapFontTextView;
  private readonly helpsLabel: LangBitmapFontTextView;
  private readonly helpsFrame: ImageScaleView;
  private readonly helpsCount: LangBitmapFontTextView;

  constructor(opts: TeamMemberEntryOpts) {
    super({
      width: 700,
      height: 100,
      ...opts,
      onClick: opts.onEntryClick,
      tracking: {
        feature: Feature.TEAM._,
        $subFeature: Feature.TEAM.MEMBER_ENTRY,
      },
    });

    this.bg = new ImageScaleView({
      superview: this,
      width: this.style.width,
      height: this.style.height + 7,
      image: 'assets/ui/match2/common/list_entry.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 22, right: 22 },
        vertical: { top: 22, bottom: 22 },
      },
    });

    this.name = new LangBitmapFontTextView({
      superview: this,
      x: 165,
      y: 0,
      align: 'left',
      verticalAlign: 'center',
      size: 34,
      font: 'Body',
      width: 281,
      height: this.style.height,
      color: '#78263E',
    });

    this.leader = new View({
      superview: this,
      width: 160,
      height: 30,
      x: 165,
      y: 55,
      visible: false,
    });
    new ImageView({
      superview: this.leader,
      image: `assets/ui/match2/team/frames/icon_leader.png`,
      width: 36,
      height: 27,
    });
    this.leaderLabel = new LangBitmapFontTextView({
      superview: this.leader,
      x: 36,
      y: 0,
      align: 'left',
      verticalAlign: 'center',
      size: 25,
      font: 'Body',
      width: 120,
      height: 30,
      color: '#BF8147',
      localeText: () => i18n('match2.team.leader'),
    });

    this.positionText = new LangBitmapFontTextView({
      superview: this,
      x: 40,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 26,
      font: 'Title',
      width: this.style.height,
      height: this.style.height,
    });

    this.positionIcon = new ImageScaleView({
      superview: this,
      x: 40,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      align: 'center',
      verticalAlign: 'center',
      width: 62,
      height: 62,
    });

    this.avatar = new Avatar({
      superview: this,
      width: 80,
      height: 80,
      x: 120,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
    });

    this.helpsLabel = new LangBitmapFontTextView({
      superview: this,
      x: 490,
      y: 10,
      align: 'center',
      verticalAlign: 'center',
      size: 24,
      font: 'Body',
      width: 90,
      height: 30,
      color: '#BF8147',
      localeText: () => i18n('match2.team.helps'),
    });

    this.helpsFrame = new ImageScaleView({
      superview: this,
      image: 'assets/ui/match2/team/frames/panel_list_beige_inset.png',
      width: 90,
      height: 40,
      x: 490,
      y: 45,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 10, right: 10 },
        vertical: { top: 10, bottom: 10 },
      },
    });

    this.helpsCount = new LangBitmapFontTextView({
      superview: this.helpsFrame,
      align: 'center',
      verticalAlign: 'center',
      size: 28,
      font: 'Body',
      width: this.helpsFrame.style.width,
      height: this.helpsFrame.style.height,
      color: '#df5266',
      localeText: () => `999`,
    });

    this.teamLabel = new LangBitmapFontTextView({
      superview: this,
      x: 610,
      y: 10,
      align: 'center',
      verticalAlign: 'center',
      size: 24,
      font: 'Body',
      width: 60,
      height: 30,
      color: '#BF8147',
      localeText: () => i18n('profile.level'),
    });

    this.level = new LangBitmapFontTextView({
      superview: this,
      x: 610,
      y: 45,
      align: 'center',
      verticalAlign: 'center',
      size: 28,
      font: 'Title',
      width: 60,
      height: 34,
      color: '#ffffff',
      localeText: () => `999`,
    });
  }

  public setup(data: TeamMemberData) {
    const { profile, position, level, helps, me } = data;

    this.name.localeText = () => profile.name;
    const isLeader = profile.role === 'admin';
    this.leader.updateOpts({ visible: isLeader });
    this.name.updateOpts({ y: isLeader ? -20 : 0 });

    if (position >= 1 && position <= 3) {
      this.positionIcon.updateOpts({
        image: `assets/ui/match2/tournament/star_tournament_medal_${position}.png`,
        visible: true,
      });
      this.positionText.updateOpts({ visible: false });
    } else {
      this.positionText.updateOpts({ visible: true });
      this.positionIcon.updateOpts({ visible: false });
      this.positionText.localeText = () => position.toString();
    }

    this.avatar.updateImage(profile.avatar);
    this.avatar.setFrame(profile.frame);
    this.level.localeText = () => level.toString();
    this.helpsCount.localeText = () => helps.toString();

    if (me) {
      this.helpsFrame.updateOpts({
        image: 'assets/ui/match2/team/frames/panel_list_orange_inset.png',
      });
      this.bg.updateOpts({
        image: 'assets/ui/match2/team/frames/panel_list_orange.png',
      });
      this.name.updateOpts({
        font: 'Title',
        color: '#ffffff',
      });
      this.teamLabel.updateOpts({
        color: '#A05A16',
      });
      this.leaderLabel.updateOpts({
        color: '#A05A16',
      });
      this.helpsLabel.updateOpts({
        color: '#A05A16',
      });
      return;
    }

    this.helpsFrame.updateOpts({
      image: 'assets/ui/match2/team/frames/panel_list_beige_inset.png',
    });
    this.bg.updateOpts({
      image: 'assets/ui/match2/common/list_entry.png',
    });
    this.name.updateOpts({
      font: 'Body',
      color: '#78263E',
    });
    this.teamLabel.updateOpts({
      color: '#BF8147',
    });
    this.leaderLabel.updateOpts({
      color: '#BF8147',
    });
    this.helpsLabel.updateOpts({
      color: '#BF8147',
    });
  }
}
